import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CompanyCertificatesRegister } from "./CompanyCertificatesRegister";
import { toast } from "sonner";

async function blobErrorDetail(e) {
  try {
    const data = e?.response?.data;
    if (data instanceof Blob) { const j = JSON.parse(await data.text()); return typeof j.detail === "string" ? j.detail : j.detail?.message || `HTTP ${e.response.status}`; }
    return typeof data?.detail === "string" ? data.detail : e?.message || "Request failed";
  } catch { return `HTTP ${e?.response?.status || "error"}`; }
}
import { motion, AnimatePresence } from "framer-motion";
import {
  Vault, Upload, Trash2, Eye, Download, FileText, Landmark, Send, Loader2,
  CheckCircle2, ShieldCheck, Sparkles, ChevronRight, X, ArchiveIcon,
  BadgeCheck, MailCheck, AlertTriangle, Radar, Scroll, Stamp,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

/**
 * Admin · Vinci Treasury Onboarding · Institutional Onboarding Cannon.
 *
 *   ┌─────────────────────── Identity Vault ────────────────────────┐
 *   │ 7 canonical slots (GST / PAN / IEC / Rent / Board Res. /    │
 *   │ MoA-AoA / Cheque) + Directors' KYC. Upload / preview / delete.│
 *   └───────────────────────────────────────────────────────────────┘
 *   ┌────────────── Dispatch (Bank Application) ────────────────────┐
 *   │ Bank + branch + manager → preview cover PDF → transmit ZIP.   │
 *   └───────────────────────────────────────────────────────────────┘
 *   ┌───────────────────── Dispatch History ────────────────────────┐
 */
export function AdminTreasuryOnboarding({ api }) {
  const [vault, setVault] = useState({ slots: [], by_slot: {} });
  const [dispatches, setDispatches] = useState([]);
  const [seal, setSeal] = useState({ present: false });
  const [stamps, setStamps] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [preview, setPreview] = useState(null);   // { doc, url }
  const [pdfPreview, setPdfPreview] = useState(null);   // blob URL
  const [transmitting, setTransmitting] = useState(false);
  // Notary picker — { title, next: (stampsArr) => Promise }
  const [picker, setPicker] = useState(null);
  const [form, setForm] = useState({
    bank_name: "", branch_address: "", manager_name: "",
    manager_designation: "Branch Manager", manager_email: "",
    doc_ids: [], extra_notes: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, d, s, st] = await Promise.all([
        api.get("/admin/treasury-onboarding/vault"),
        api.get("/admin/treasury-onboarding/bundles"),
        api.get("/admin/treasury-onboarding/seal"),
        api.get("/admin/treasury-onboarding/stamps"),
      ]);
      setVault(v.data || { slots: [], by_slot: {} });
      setDispatches(d.data?.rows || []);
      setSeal(s.data || { present: false });
      setStamps(st.data || {});
    } catch { toast.error("Could not load treasury onboarding vault"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // ── Vault ops ─────────────────────────────────────────────────
  const uploadFor = async (slot, file, opts = {}) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File exceeds 15 MB — compress before uploading");
      return;
    }
    const key = opts.side ? `${slot}:${opts.side}` : slot;
    setUploadingSlot(key);
    try {
      const fd = new FormData();
      fd.append("slot", slot);
      fd.append("file", file);
      if (opts.side) fd.append("side", opts.side);
      if (opts.custom_label) fd.append("custom_label", opts.custom_label);
      await api.post("/admin/treasury-onboarding/vault", fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(opts.side
        ? `${opts.side === "front" ? "Front" : "Back"} page sealed`
        : "Uploaded · sealed and verified");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploadingSlot(null); }
  };

  const renameDoc = async (docId, custom_label) => {
    try {
      await api.patch(`/admin/treasury-onboarding/vault/${docId}/label`,
                      { custom_label });
      toast.success("Label saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    }
  };

  const removeDoc = async (docId) => {
    if (!confirm("Delete this document from the identity vault?")) return;
    try {
      await api.delete(`/admin/treasury-onboarding/vault/${docId}`);
      toast.success("Document removed");
      setForm((f) => ({ ...f, doc_ids: f.doc_ids.filter((i) => i !== docId) }));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const previewDoc = async (doc) => {
    try {
      const r = await api.get(
        `/admin/treasury-onboarding/vault/${doc.id}/preview`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      setPreview({ doc, url });
    } catch (e) { toast.error("Could not preview file", { description: await blobErrorDetail(e), duration: 9000 }); }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await api.get(
        `/admin/treasury-onboarding/vault/${doc.id}/preview`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || `vinci-${doc.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success(`Downloading ${doc.filename}`);
    } catch (e) { toast.error("Could not download file", { description: await blobErrorDetail(e), duration: 9000 }); }
  };

  // ── Dispatch flow ─────────────────────────────────────────────
  const toggleDoc = (docId) => setForm((f) => ({
    ...f,
    doc_ids: f.doc_ids.includes(docId)
      ? f.doc_ids.filter((i) => i !== docId)
      : [...f.doc_ids, docId],
  }));

  const selectAllRequired = () => {
    // pick the most-recent doc for each required slot
    const picks = [];
    for (const s of vault.slots || []) {
      if (!s.required) continue;
      const docs = (vault.by_slot?.[s.slot] || []).filter((d) => !d.storage_missing);
      if (s.paired) {
        for (const side of ["front", "back"]) { const d = docs.find((x) => x.side === side); if (d) picks.push(d.id); }
      } else if (docs.length) picks.push(docs[0].id);
    }
    setForm((f) => ({ ...f, doc_ids: picks }));
  };

  const buildPayload = (stampsArr = []) => ({
    bank_name: form.bank_name.trim(),
    branch_address: form.branch_address.trim(),
    manager_name: form.manager_name.trim(),
    manager_designation: (form.manager_designation || "Branch Manager").trim(),
    manager_email: form.manager_email.trim(),
    doc_ids: form.doc_ids,
    extra_notes: form.extra_notes,
    stamps: stampsArr,
  });

  // Required-slot enforcement — mirrors backend missing_required(): every required slot
  // (both Aadhaar sides) must have an uploaded file AND be attached to this dispatch.
  const requiredGaps = useMemo(() => {
    const missing = [], unattached = [];
    for (const s of vault.slots || []) {
      if (!s.required) continue;
      const docs = vault.by_slot?.[s.slot] || [];
      const sides = s.paired ? ["front", "back"] : [null];
      for (const side of sides) {
        const pool = (side ? docs.filter((d) => d.side === side) : docs).filter((d) => !d.storage_missing);
        const label = side ? `${s.label} · ${side[0].toUpperCase()}${side.slice(1)}` : s.label;
        if (!pool.length) missing.push(label);
        else if (!pool.some((d) => form.doc_ids.includes(d.id))) unattached.push(label);
      }
    }
    return { missing, unattached };
  }, [vault, form.doc_ids]);
  const packetComplete = requiredGaps.missing.length === 0 && requiredGaps.unattached.length === 0;

  const canTransmit = form.bank_name && form.branch_address
                        && form.manager_name && form.manager_email && packetComplete;

  // Notary presence — round + (chorus OR signed composite)
  const hasAnyStamp = !!(stamps?.round?.present
                            || stamps?.chorus?.present
                            || stamps?.signed?.present);

  const askStamps = (title, blurb) => new Promise((resolve) => {
    if (!hasAnyStamp) { resolve([]); return; }
    setPicker({
      title, blurb,
      onPick: (picked) => { setPicker(null); resolve(picked); },
      onCancel: () => { setPicker(null); resolve(null); },
    });
  });

  const previewCoverLetter = async () => {
    if (!form.bank_name || !form.manager_name) {
      toast.error("Enter the bank + manager name first");
      return;
    }
    const picked = await askStamps(
      "Notarise Cover Letter",
      "Choose which impression to affix onto the signature block.",
    );
    if (picked === null) return; // cancelled
    try {
      const r = await api.post(
        "/admin/treasury-onboarding/bundles/cover-preview",
        buildPayload(picked),
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      setPdfPreview(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
  };

  const transmit = async () => {
    if (form.doc_ids.length === 0) {
      toast.error("Attach at least one dossier document");
      return;
    }
    if (!packetComplete) {
      toast.error("Dispatch blocked — packet incomplete", { description: [...requiredGaps.missing, ...requiredGaps.unattached].join(" · ") });
      return;
    }
    const picked = await askStamps(
      "Notarise Before Transmit",
      "Every attached document ships with the impression you pick here.",
    );
    if (picked === null) return;
    setTransmitting(true);
    try {
      const r = await api.post(
        "/admin/treasury-onboarding/bundles/transmit",
        buildPayload(picked),
      );
      if (r.data?.mail_status === "sent") {
        toast.success("Sealed bundle transmitted", {
          description: `Delivered to ${form.manager_email} · Ref ${r.data.dispatch.ref}`,
        });
      } else {
        toast.warning("Bundle logged but email failed", {
          description: r.data?.mail_error || "Retry from history",
        });
      }
      setForm({
        bank_name: "", branch_address: "", manager_name: "",
        manager_designation: "Branch Manager", manager_email: "",
        doc_ids: [], extra_notes: "",
      });
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.missing) {
        toast.error(d.message, { description: d.missing.map((m) => m.label).join(" · "), duration: 12000 });
        await load();
      } else toast.error(typeof d === "string" ? d : "Transmit failed");
    } finally { setTransmitting(false); }
  };

  const totalDocs = useMemo(
    () => Object.values(vault.by_slot || {}).reduce((n, arr) => n + arr.length, 0),
    [vault.by_slot],
  );

  // ── Auto-generate Board Resolution ─────────────────────────────
  const [resPreview, setResPreview] = useState(null);
  const [resBusy, setResBusy] = useState(false);

  const renderResolution = async () => {
    if (!form.bank_name || !form.branch_address) {
      toast.error("Enter Bank Name + Branch Address first");
      return;
    }
    const picked = await askStamps(
      "Notarise Board Resolution",
      "This decree is a senior-management document. Pick the impression.",
    );
    if (picked === null) return;
    setResBusy(true);
    try {
      const r = await api.post(
        "/admin/treasury-onboarding/board-resolution/render",
        { bank_name: form.bank_name.trim(),
           branch_address: form.branch_address.trim(),
           stamps: picked },
        { responseType: "blob" },
      );
      setResPreview(URL.createObjectURL(r.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not render resolution");
    } finally { setResBusy(false); }
  };

  const saveResolutionToVault = async () => {
    if (!form.bank_name || !form.branch_address) {
      toast.error("Enter Bank Name + Branch Address first");
      return;
    }
    const picked = await askStamps(
      "Notarise & Seal Into Vault",
      "The stored resolution will carry this impression on every future dispatch.",
    );
    if (picked === null) return;
    setResBusy(true);
    try {
      const r = await api.post(
        "/admin/treasury-onboarding/board-resolution/save",
        { bank_name: form.bank_name.trim(),
           branch_address: form.branch_address.trim(),
           stamps: picked },
      );
      toast.success(r.data.replaced ? "Resolution regenerated — previous auto-generated copy replaced" : "Resolution sealed into the vault", {
        description: `Ref ${r.data.resolution_number}`,
      });
      // Auto-attach the freshly-saved doc; drop ids of the replaced copies
      const newId = r.data?.doc?.id;
      const oldAutoIds = (vault.by_slot?.board_resolution || []).filter((d) => d.auto_generated).map((d) => d.id);
      if (newId) setForm((f) => ({ ...f, doc_ids: [...new Set([...f.doc_ids.filter((i) => !oldAutoIds.includes(i)), newId])] }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save resolution");
    } finally { setResBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-treasury-onboarding">
      <CompanyCertificatesRegister api={api} />
      <HeaderCard totalDocs={totalDocs} />

      {/* ═══════════════════ Identity Vault ═══════════════════ */}
      <div className="space-y-3">
        <SectionHeading eyebrow="Section I · Master Identity"
                        title="The Vinci Identity Vault"
                        blurb="Every mandatory statutory document, sealed in the vault and previewable in one click." />
        {loading ? (
          <div className="text-center py-10 text-[#8A8A82]">
            <Loader2 className="animate-spin mx-auto" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="vault-grid">
            {(vault.slots || []).map((slot) => (
              <VaultSlotCard
                key={slot.slot}
                slot={slot}
                docs={vault.by_slot?.[slot.slot] || []}
                onUpload={(file, opts) => uploadFor(slot.slot, file, opts)}
                onPreview={previewDoc}
                onDownload={downloadDoc}
                onDelete={removeDoc}
                onAttach={toggleDoc}
                onRename={renameDoc}
                selectedIds={form.doc_ids}
                uploadingKey={uploadingSlot}
              />
            ))}
          </div>
        )}
      </div>

      {/* ═══════════════════ Corporate Seal ═══════════════════ */}
      <div className="space-y-3">
        <SectionHeading eyebrow="Section I·b · Sovereign Seal"
                        title="Corporate Seal Impression"
                        blurb="Upload a scanned PNG of the physical Vinci seal. It will be automatically painted inside the glowing gold ring on every auto-generated Board Resolution." />
        <CorporateSealPanel api={api} seal={seal} onRefresh={load} />
      </div>

      {/* ═══════════════════ Digital Notary ═══════════════════ */}
      <div className="space-y-3">
        <SectionHeading eyebrow="Section I·c · Digital Notary"
                        title="Rubber-Stamp Vault"
                        blurb="Upload the physical Round + Chorus rubber stamps and the proprietor pen signature. The refiner drops the paper, auto-rotates and centres the composite so every Resolution & Cover Letter can carry the real ink." />
        <StampGalleryPanel api={api} stamps={stamps} onRefresh={load} />
      </div>

      {/* ═══════════════════ Dispatch Form ═══════════════════ */}
      <div className="space-y-3">
        <SectionHeading eyebrow="Section II · Application Cannon"
                        title="Address a Bank"
                        blurb="Enter the target institution + branch manager, attach the dossier, and fire a sealed application." />
        <div className="border border-[#D8B23E]/30 rounded-sm bg-gradient-to-br from-[#0A0906] via-[#12100A] to-[#0A0906] p-6 md:p-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Bank Name *" testid="onb-bank-name">
              <Input data-testid="onb-bank-name"
                     value={form.bank_name}
                     onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                     placeholder="HDFC Bank Ltd."
                     className="bg-black/40 border-white/20 mt-1" />
            </Field>
            <Field label="Manager Name *" testid="onb-manager-name">
              <Input data-testid="onb-manager-name"
                     value={form.manager_name}
                     onChange={(e) => setForm({ ...form, manager_name: e.target.value })}
                     placeholder="Mr. Ramesh Iyer"
                     className="bg-black/40 border-white/20 mt-1" />
            </Field>
            <Field label="Manager Designation" testid="onb-manager-designation">
              <Input data-testid="onb-manager-designation"
                     value={form.manager_designation}
                     onChange={(e) => setForm({ ...form, manager_designation: e.target.value })}
                     placeholder="Branch Manager"
                     className="bg-black/40 border-white/20 mt-1" />
            </Field>
            <Field label="Manager Email *" testid="onb-manager-email">
              <Input data-testid="onb-manager-email" type="email"
                     value={form.manager_email}
                     onChange={(e) => setForm({ ...form, manager_email: e.target.value })}
                     placeholder="manager.chandivali@hdfcbank.com"
                     className="bg-black/40 border-white/20 mt-1" />
            </Field>
          </div>
          <Field label="Branch Address *" testid="onb-branch-address">
            <Textarea data-testid="onb-branch-address"
                       value={form.branch_address}
                       onChange={(e) => setForm({ ...form, branch_address: e.target.value })}
                       placeholder="Ground Floor, XYZ Chambers, Saki Vihar Road, Chandivali, Mumbai 400 072"
                       className="bg-black/40 border-white/20 mt-1 min-h-[80px]" />
          </Field>
          <Field label="Additional Notes (optional)" testid="onb-notes">
            <Textarea data-testid="onb-notes"
                       value={form.extra_notes}
                       onChange={(e) => setForm({ ...form, extra_notes: e.target.value })}
                       placeholder="Any bespoke request to include in the cover letter"
                       className="bg-black/40 border-white/20 mt-1 min-h-[60px]" />
          </Field>

          {!packetComplete && (
            <div data-testid="onb-missing-required" className="border border-[#E08A8A]/50 bg-[#2A0F0F]/40 rounded-sm p-4">
              <div className="text-[10px] font-grotesk uppercase tracking-[0.26em] text-[#E08A8A] inline-flex items-center gap-2"><AlertTriangle size={12} /> Dispatch blocked · packet incomplete</div>
              {requiredGaps.missing.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] font-grotesk uppercase tracking-[0.2em] text-[#C9C9C2]">Required · not uploaded (or file missing in storage)</div>
                  <ul className="mt-1 space-y-0.5">{requiredGaps.missing.map((m) => <li key={m} className="text-[12px] text-[#F5F5F0] font-body" data-testid="onb-missing-item">◇ {m}</li>)}</ul>
                </div>
              )}
              {requiredGaps.unattached.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] font-grotesk uppercase tracking-[0.2em] text-[#C9C9C2]">Uploaded but not attached to this dispatch</div>
                  <ul className="mt-1 space-y-0.5">{requiredGaps.unattached.map((m) => <li key={m} className="text-[12px] text-[#D8B23E] font-body" data-testid="onb-unattached-item">◆ {m}</li>)}</ul>
                </div>
              )}
            </div>
          )}
          <div className="border-t border-white/10 pt-4 flex flex-wrap items-center gap-3">
            <div className="text-[11px] font-grotesk uppercase tracking-[0.22em] text-[#D8B23E] inline-flex items-center gap-2" data-testid="onb-attached-count">
              <ArchiveIcon size={12} /> {form.doc_ids.length} document{form.doc_ids.length === 1 ? "" : "s"} attached
              {packetComplete && <span className="text-[#2FB999]" data-testid="onb-packet-complete">· all required present</span>}
            </div>
            <Button data-testid="onb-select-required"
                     onClick={selectAllRequired}
                     variant="outline" size="sm"
                     className="border-white/20 text-[#C9C9C2] text-[10px] font-grotesk uppercase tracking-[0.22em]">
              Attach all required
            </Button>
            <Button data-testid="onb-render-resolution"
                     onClick={renderResolution}
                     disabled={resBusy || !form.bank_name || !form.branch_address}
                     variant="outline" size="sm"
                     className="border-[#D8B23E]/50 text-[#D8B23E] hover:bg-[#D8B23E]/10 text-[10px] font-grotesk uppercase tracking-[0.22em]">
              {resBusy ? <Loader2 size={11} className="animate-spin" /> : <><Scroll size={11} className="mr-1" /> Preview Resolution</>}
            </Button>
            <Button data-testid="onb-save-resolution"
                     onClick={saveResolutionToVault}
                     disabled={resBusy || !form.bank_name || !form.branch_address}
                     size="sm"
                     className="bg-[#D8B23E]/20 border border-[#D8B23E] text-[#D8B23E] hover:bg-[#D8B23E]/30 text-[10px] font-grotesk uppercase tracking-[0.22em]">
              <Stamp size={11} className="mr-1" /> Auto-Generate & Seal
            </Button>
            <div className="flex-1" />
            <Button data-testid="onb-preview-cover"
                     onClick={previewCoverLetter}
                     variant="outline"
                     className="border-[#D8B23E]/50 text-[#D8B23E] hover:bg-[#D8B23E]/10 font-grotesk uppercase tracking-[0.24em] text-[10px]">
              <FileText size={12} className="mr-1.5" /> Preview Cover Letter
            </Button>
            <Button data-testid="onb-transmit"
                     onClick={transmit}
                     disabled={!canTransmit || transmitting || form.doc_ids.length === 0}
                     title={packetComplete ? "" : "Attach every required document first"}
                     className="text-black font-grotesk uppercase tracking-[0.28em] text-[11px] rounded-sm px-6 py-5 disabled:opacity-40 disabled:cursor-not-allowed"
                     style={{
                       background: "linear-gradient(90deg,#D8B23E,#F6E4A6,#D8B23E)",
                       boxShadow: "0 0 30px rgba(216,178,62,0.35)",
                     }}>
              {transmitting
                ? <><Loader2 size={13} className="animate-spin mr-2" /> Transmitting…</>
                : <><Send size={13} className="mr-2" /> Transmit Sealed Bundle</>}
            </Button>
          </div>
        </div>
      </div>

      {/* ═══════════════════ History ═══════════════════ */}
      <div className="space-y-3">
        <SectionHeading eyebrow="Section III · Fired Rounds"
                        title="Dispatch Ledger"
                        blurb="Every bank application fired from this desk, with delivery status." />
        {dispatches.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-sm p-8 text-center text-[#6f6f68] font-body italic">
            No bank applications transmitted yet.
          </div>
        ) : (
          <div className="space-y-3" data-testid="onb-history">
            {dispatches.map((d) => (
              <DispatchRow key={d.id} d={d} />
            ))}
          </div>
        )}
      </div>

      {/* ═══════════════════ Modals ═══════════════════ */}
      <PreviewDialog preview={preview}
                       onDownload={downloadDoc}
                       onClose={() => {
        if (preview?.url) URL.revokeObjectURL(preview.url);
        setPreview(null);
      }} />

      <Dialog open={!!pdfPreview} onOpenChange={(v) => {
        if (!v && pdfPreview) URL.revokeObjectURL(pdfPreview);
        if (!v) setPdfPreview(null);
      }}>
        <DialogContent
          data-testid="onb-cover-preview-dialog"
          className="bg-[#050505] border border-[#D8B23E]/60 text-[#F5F5F0] max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
              <FileText size={18} className="text-[#D8B23E]" />
              Cover Letter Preview
            </DialogTitle>
            <DialogDescription className="text-[12px] font-body text-[#C9C9C2]">
              Draft PDF · not yet transmitted. Close, tweak, and re-preview any time.
            </DialogDescription>
          </DialogHeader>
          {pdfPreview && (
            <iframe title="Cover letter" src={pdfPreview}
                     className="w-full h-[70vh] rounded-sm border border-white/10 bg-black" />
          )}
          <DialogFooter>
            <Button
              data-testid="onb-cover-preview-close"
              onClick={() => {
                if (pdfPreview) URL.revokeObjectURL(pdfPreview);
                setPdfPreview(null);
              }}
              className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.24em] text-[10px]"
            >
              Close Preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Board Resolution preview */}
      <Dialog open={!!resPreview} onOpenChange={(v) => {
        if (!v && resPreview) URL.revokeObjectURL(resPreview);
        if (!v) setResPreview(null);
      }}>
        <DialogContent
          data-testid="onb-resolution-preview-dialog"
          className="bg-[#050505] border border-[#D8B23E]/70 text-[#F5F5F0] max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
              <Scroll size={18} className="text-[#D8B23E]" />
              Executive Resolution of Banking
            </DialogTitle>
            <DialogDescription className="text-[12px] font-body text-[#C9C9C2]">
              Sovereign decree · the Vinci Common Seal pre-affixed · Corporate seal placeholder rendered.
            </DialogDescription>
          </DialogHeader>
          {resPreview && (
            <iframe title="Banking Resolution" src={resPreview}
                     className="w-full h-[70vh] rounded-sm border border-white/10 bg-black" />
          )}
          <DialogFooter className="gap-2">
            <Button
              data-testid="onb-resolution-close"
              variant="outline"
              onClick={() => {
                if (resPreview) URL.revokeObjectURL(resPreview);
                setResPreview(null);
              }}
              className="border-white/20 text-[#C9C9C2] font-grotesk uppercase tracking-[0.24em] text-[10px]"
            >
              Close
            </Button>
            <Button
              data-testid="onb-resolution-save"
              onClick={async () => {
                await saveResolutionToVault();
                if (resPreview) URL.revokeObjectURL(resPreview);
                setResPreview(null);
              }}
              disabled={resBusy}
              className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.24em] text-[10px]"
            >
              {resBusy ? <Loader2 size={11} className="animate-spin" /> : <><Stamp size={11} className="mr-1" /> Seal into Vault</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stamp picker */}
      <StampPickerDialog picker={picker} stamps={stamps} api={api} />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Sub-components
// ═════════════════════════════════════════════════════════════════════
function HeaderCard({ totalDocs }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="relative overflow-hidden border border-[#D8B23E]/40 rounded-sm p-6 md:p-8"
      style={{
        background: "linear-gradient(135deg,#0A0906 0%,#1B1401 55%,#0A0906 100%)",
        boxShadow: "0 0 80px -20px rgba(216,178,62,0.35)",
      }}
    >
      <motion.div aria-hidden
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{ background: "conic-gradient(from 0deg, transparent 0deg, rgba(216,178,62,0.15) 90deg, transparent 180deg, rgba(216,178,62,0.15) 270deg, transparent 360deg)" }}
        animate={{ rotate: 360 }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }} />
      <div className="relative flex items-start gap-4">
        <div className="w-14 h-14 rounded-full grid place-items-center bg-[#D8B23E]/15 border border-[#D8B23E]/40 shrink-0">
          <Vault size={24} className="text-[#D8B23E]" />
        </div>
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#D8B23E]">
            Vinci Finance Desk · Institutional Onboarding Cannon
          </div>
          <h2 className="font-serif text-3xl md:text-4xl mt-2 text-[#F5F5F0]">
            Banking Onboarding
          </h2>
          <p className="text-[13px] text-[#C9C9C2] font-body mt-2 leading-relaxed max-w-3xl">
            A high-trust dispatch console. Curate your master identity dossier,
            address any bank in India, and fire a sealed, branded application in
            a single click — cover letter, ZIP bundle, and holographic gold email
            all in one pull of the trigger.
          </p>
          <div className="mt-3 inline-flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.22em] text-[#D8B23E]">
            <span className="px-2 py-0.5 rounded-sm bg-black/40 border border-[#D8B23E]/40">
              {totalDocs} documents sealed
            </span>
            <span className="text-[#8A8A82] normal-case tracking-[0.05em]">
              · sealed · Vinci Inc. Common Seal — Authorized Signatory
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function SectionHeading({ eyebrow, title, blurb }) {
  return (
    <div>
      <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#D8B23E]">
        {eyebrow}
      </div>
      <h3 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-1.5">{title}</h3>
      {blurb && <p className="text-[13px] text-[#8A8A82] font-body mt-1 max-w-3xl">{blurb}</p>}
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div>
      <Label className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-[#D8B23E]">
        {label}
      </Label>
      {children}
    </div>
  );
}

function VaultSlotCard({ slot, docs, onUpload, onPreview, onDownload, onDelete,
                            onAttach, onRename, selectedIds, uploadingKey }) {
  const paired = !!slot.paired;
  const customLabel = !!slot.custom_label;
  if (paired) {
    return <PairedIdSlotCard
              slot={slot} docs={docs}
              onUpload={onUpload} onPreview={onPreview}
              onDownload={onDownload} onDelete={onDelete}
              onAttach={onAttach} selectedIds={selectedIds}
              uploadingKey={uploadingKey} />;
  }
  if (customLabel) {
    return <MiscSlotCard
              slot={slot} docs={docs}
              onUpload={onUpload} onPreview={onPreview}
              onDownload={onDownload} onDelete={onDelete}
              onAttach={onAttach} onRename={onRename} selectedIds={selectedIds}
              uploadingKey={uploadingKey} />;
  }
  return <SingleSlotCard
            slot={slot} docs={docs}
            onUpload={onUpload} onPreview={onPreview}
            onDownload={onDownload} onDelete={onDelete}
            onAttach={onAttach} selectedIds={selectedIds}
            uploadingKey={uploadingKey} />;
}

function SingleSlotCard({ slot, docs, onUpload, onPreview, onDownload, onDelete,
                             onAttach, selectedIds, uploadingKey }) {
  const inputRef = useRef(null);
  const hasDocs = docs.length > 0;
  const uploading = uploadingKey === slot.slot;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`vault-slot-${slot.slot}`}
      className={`border rounded-sm p-5 space-y-3 ${
        hasDocs
          ? "border-[#2FB999]/40 bg-[#02150F]/40"
          : slot.required
            ? "border-[#D8B23E]/40 bg-[#1B1401]/20"
            : "border-white/10 bg-black/20"
      }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <StatusPill hasDocs={hasDocs} required={slot.required} />
          <div className="font-serif text-lg text-[#F5F5F0] mt-1">{slot.label}</div>
          {slot.note && <div className="text-[10px] text-[#8A8A82] font-body italic mt-0.5" data-testid={`vault-note-${slot.slot}`}>{slot.note}</div>}
        </div>
        <input ref={inputRef} type="file" className="hidden"
               data-testid={`vault-input-${slot.slot}`}
               onChange={(e) => onUpload(e.target.files?.[0])} />
        <Button variant="outline" size="sm"
                 data-testid={`vault-upload-${slot.slot}`}
                 onClick={() => inputRef.current?.click()}
                 disabled={uploading}
                 className="border-[#D8B23E]/40 text-[#D8B23E] hover:bg-[#D8B23E]/10 text-[10px] font-grotesk uppercase tracking-[0.22em] shrink-0">
          {uploading
            ? <Loader2 size={12} className="animate-spin" />
            : <><Upload size={12} className="mr-1" /> {hasDocs ? "Replace" : "Upload"}</>}
        </Button>
      </div>
      <DocRows docs={docs} selectedIds={selectedIds}
                onPreview={onPreview} onDownload={onDownload}
                onDelete={onDelete} onAttach={onAttach} />
    </motion.div>
  );
}

function PairedIdSlotCard({ slot, docs, onUpload, onPreview, onDownload, onDelete,
                                onAttach, selectedIds, uploadingKey }) {
  const frontDoc = docs.find((d) => d.side === "front");
  const backDoc = docs.find((d) => d.side === "back");
  const hasBoth = !!frontDoc && !!backDoc;
  const status = hasBoth ? "verified" : (frontDoc || backDoc) ? "partial" : "empty";
  const frontRef = useRef(null);
  const backRef = useRef(null);
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`vault-slot-${slot.slot}`}
      className={`border rounded-sm p-5 space-y-3 ${
        status === "verified"
          ? "border-[#2FB999]/40 bg-[#02150F]/40"
          : status === "partial"
            ? "border-[#E5A93E]/60 bg-[#1B1401]/40"
            : slot.required ? "border-[#D8B23E]/40 bg-[#1B1401]/20"
                             : "border-white/10 bg-black/20"
      }`}>
      <div className="min-w-0">
        <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] flex items-center gap-2"
              style={{ color:
                status === "verified" ? "#2FB999"
                : status === "partial" ? "#E5A93E"
                : slot.required ? "#D8B23E" : "#8A8A82" }}>
          {status === "verified"
            ? <><BadgeCheck size={11} /> Stitched · Front + Back Verified</>
            : status === "partial"
              ? <><AlertTriangle size={11} /> Half-uploaded — the other side is missing</>
              : <><AlertTriangle size={11} /> Required — no images uploaded</>}
        </div>
        <div className="font-serif text-lg text-[#F5F5F0] mt-1">{slot.label}</div>
        <div className="text-[10px] text-[#8A8A82] font-body mt-0.5 italic">
          On dispatch, both pages are auto-stitched into a single 2-page verification PDF.
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <SideUploadTile
          label="Front Page" side="front" doc={frontDoc}
          uploading={uploadingKey === `${slot.slot}:front`}
          onClick={() => frontRef.current?.click()}
          onPreview={onPreview} onDownload={onDownload} onDelete={onDelete}
          slotKey={slot.slot} />
        <SideUploadTile
          label="Back Page" side="back" doc={backDoc}
          uploading={uploadingKey === `${slot.slot}:back`}
          onClick={() => backRef.current?.click()}
          onPreview={onPreview} onDownload={onDownload} onDelete={onDelete}
          slotKey={slot.slot} />
        <input ref={frontRef} type="file" className="hidden"
               data-testid={`vault-input-${slot.slot}-front`}
               onChange={(e) => onUpload(e.target.files?.[0], { side: "front" })} />
        <input ref={backRef} type="file" className="hidden"
               data-testid={`vault-input-${slot.slot}-back`}
               onChange={(e) => onUpload(e.target.files?.[0], { side: "back" })} />
      </div>
      {/* Attach toggle — activates only when both sides are present */}
      {docs.length > 0 && (
        <div className="pt-2 border-t border-white/5">
          {docs.map((d) => {
            const attached = selectedIds.includes(d.id);
            return (
              <button key={d.id}
                       data-testid={`vault-attach-${d.id}`}
                       onClick={() => onAttach(d.id)}
                       className={`text-[10px] font-grotesk uppercase tracking-[0.22em] px-2 py-1 mr-2 rounded-sm border ${
                         attached
                           ? "border-[#D8B23E] text-[#D8B23E] bg-[#D8B23E]/10"
                           : "border-white/10 text-[#8A8A82] hover:text-[#D8B23E] hover:border-[#D8B23E]/40"
                       }`}>
                {attached ? "Attached" : "Attach"} · {d.side}
              </button>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}

function SideUploadTile({ label, side, doc, uploading, onClick,
                              onPreview, onDownload, onDelete, slotKey }) {
  return (
    <div
      data-testid={`vault-side-${slotKey}-${side}`}
      className={`rounded-sm p-3 border ${
        doc ? "border-[#2FB999]/40 bg-[#02150F]/40"
             : "border-dashed border-[#D8B23E]/30 bg-black/30"
      }`}>
      <div className="flex items-center justify-between">
        <div className="font-grotesk text-[9px] uppercase tracking-[0.22em]"
              style={{ color: doc ? "#2FB999" : "#D8B23E" }}>
          {label}
        </div>
        {doc && <CheckCircle2 size={13} className="text-[#2FB999]" />}
      </div>
      {doc ? (
        <div className="mt-2">
          <div className="text-[11px] text-[#F5F5F0] truncate">{doc.filename}</div>
          <div className="text-[9px] text-[#8A8A82] font-mono">
            {(doc.size_bytes / 1024).toFixed(0)} KB
          </div>
          {doc.storage_missing && <div data-testid={`vault-doc-missing-${doc.id}`} className="mt-1 text-[9px] font-grotesk uppercase tracking-[0.18em] text-[#E08A8A]">File missing in storage — re-upload</div>}
          <div className="mt-2 flex items-center gap-1.5">
            <button data-testid={`vault-preview-${doc.id}`}
                     onClick={() => onPreview(doc)}
                     className="p-1 text-[#8A8A82] hover:text-[#D8B23E]"
                     title="Preview">
              <Eye size={12} />
            </button>
            <button data-testid={`vault-download-${doc.id}`}
                     onClick={() => onDownload(doc)}
                     className="p-1 text-[#8A8A82] hover:text-[#2FB999]"
                     title="Download">
              <Download size={12} />
            </button>
            <button data-testid={`vault-delete-${doc.id}`}
                     onClick={() => onDelete(doc.id)}
                     className="p-1 text-[#8A8A82] hover:text-[#E5484D]"
                     title="Delete">
              <Trash2 size={12} />
            </button>
            <button data-testid={`vault-replace-${slotKey}-${side}`}
                     onClick={onClick}
                     className="p-1 text-[#8A8A82] hover:text-[#D8B23E]"
                     title="Replace">
              <Upload size={12} />
            </button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="outline"
                 data-testid={`vault-upload-${slotKey}-${side}`}
                 onClick={onClick} disabled={uploading}
                 className="w-full mt-2 border-[#D8B23E]/40 text-[#D8B23E] hover:bg-[#D8B23E]/10 text-[9px] font-grotesk uppercase tracking-[0.22em]">
          {uploading
            ? <Loader2 size={11} className="animate-spin" />
            : <><Upload size={11} className="mr-1" /> Upload {side === "front" ? "Front" : "Back"}</>}
        </Button>
      )}
    </div>
  );
}

function MiscSlotCard({ slot, docs, onUpload, onPreview, onDownload, onDelete,
                            onAttach, onRename, selectedIds, uploadingKey }) {
  const inputRef = useRef(null);
  const existing = docs[0];
  const [label, setLabel] = useState(existing?.custom_label || "");
  useEffect(() => { setLabel(existing?.custom_label || ""); },
              [existing?.custom_label]);
  const uploading = uploadingKey === slot.slot;
  const saveLabel = () => {
    if (!existing) return;
    if ((existing.custom_label || "") !== label) onRename(existing.id, label);
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`vault-slot-${slot.slot}`}
      className={`border rounded-sm p-5 space-y-3 ${
        docs.length ? "border-[#2FB999]/40 bg-[#02150F]/40"
                    : "border-dashed border-white/15 bg-black/20"
      }`}>
      <div>
        <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] flex items-center gap-2"
              style={{ color: docs.length ? "#2FB999" : "#8A8A82" }}>
          {docs.length
            ? <><BadgeCheck size={11} /> Custom Doc · Verified</>
            : <>Optional · Custom Label</>}
        </div>
        <div className="font-serif text-lg text-[#F5F5F0] mt-1">{slot.label}</div>
      </div>
      <div className="flex items-center gap-2">
        <Input value={label}
                data-testid={`vault-label-${slot.slot}`}
                onChange={(e) => setLabel(e.target.value)}
                onBlur={saveLabel}
                onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); }}}
                placeholder="Type a label (e.g. Partnership Agreement, ISO Certificate)"
                className="bg-black/40 border-white/15 text-[13px] font-body flex-1" />
        <input ref={inputRef} type="file" className="hidden"
               data-testid={`vault-input-${slot.slot}`}
               onChange={(e) => onUpload(e.target.files?.[0], { custom_label: label })} />
        <Button variant="outline" size="sm"
                 data-testid={`vault-upload-${slot.slot}`}
                 onClick={() => inputRef.current?.click()}
                 disabled={uploading}
                 className="border-[#D8B23E]/40 text-[#D8B23E] hover:bg-[#D8B23E]/10 text-[10px] font-grotesk uppercase tracking-[0.22em] shrink-0">
          {uploading
            ? <Loader2 size={12} className="animate-spin" />
            : <><Upload size={12} className="mr-1" /> {docs.length ? "Replace" : "Upload"}</>}
        </Button>
      </div>
      <DocRows docs={docs} selectedIds={selectedIds}
                onPreview={onPreview} onDownload={onDownload}
                onDelete={onDelete} onAttach={onAttach} />
    </motion.div>
  );
}

function StatusPill({ hasDocs, required }) {
  return (
    <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] flex items-center gap-2"
          style={{ color: hasDocs ? "#2FB999" : required ? "#D8B23E" : "#8A8A82" }}>
      {hasDocs
        ? <><BadgeCheck size={11} /> Verified in Vault</>
        : required
          ? <><AlertTriangle size={11} /> Required — not uploaded</>
          : <>Optional</>}
    </div>
  );
}

function DocRows({ docs, selectedIds, onPreview, onDownload, onDelete, onAttach }) {
  if (!docs.length) return null;
  return (
    <div className="space-y-2">
      {docs.map((d) => {
        const attached = selectedIds.includes(d.id);
        const labelBadge = d.custom_label && d.display_label
                              && d.display_label !== d.slot_label ? d.display_label : null;
        return (
          <div key={d.id}
                className={`flex items-center justify-between gap-2 p-2 rounded-sm text-[12px] font-body ${
                  attached ? "bg-[#D8B23E]/10 border border-[#D8B23E]/40"
                            : "bg-black/40 border border-white/5"
                }`}>
            <div className="min-w-0">
              {labelBadge && (
                <div data-testid={`vault-doc-label-${d.id}`}
                      className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-[#D8B23E] mb-0.5">
                  {labelBadge}
                </div>
              )}
              <div className="text-[#F5F5F0] truncate">{d.filename}</div>
              <div className="text-[10px] text-[#8A8A82] font-mono">
                {(d.size_bytes / 1024).toFixed(0)} KB ·{" "}
                {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ""}
                {d.auto_generated && <span className="text-[#D8B23E]"> · auto-generated</span>}
              </div>
              {d.storage_missing && (
                <div data-testid={`vault-doc-missing-${d.id}`} className="mt-1 inline-flex items-center gap-1 text-[9px] font-grotesk uppercase tracking-[0.2em] text-[#E08A8A]">
                  <AlertTriangle size={10} /> File missing in storage — re-upload
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button title="Preview"
                      data-testid={`vault-preview-${d.id}`}
                      onClick={() => onPreview(d)}
                      className="p-1.5 text-[#8A8A82] hover:text-[#D8B23E]">
                <Eye size={13} />
              </button>
              <button title="Download"
                      data-testid={`vault-download-${d.id}`}
                      onClick={() => onDownload(d)}
                      className="p-1.5 text-[#8A8A82] hover:text-[#2FB999]">
                <Download size={13} />
              </button>
              <button title={attached ? "Detach" : "Attach"}
                      data-testid={`vault-attach-${d.id}`}
                      onClick={() => onAttach(d.id)}
                      className={`p-1.5 ${attached ? "text-[#D8B23E]" : "text-[#8A8A82] hover:text-[#D8B23E]"}`}>
                {attached ? <CheckCircle2 size={13} /> : <ArchiveIcon size={13} />}
              </button>
              <button title="Delete"
                      data-testid={`vault-delete-${d.id}`}
                      onClick={() => onDelete(d.id)}
                      className="p-1.5 text-[#8A8A82] hover:text-[#E5484D]">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CorporateSealPanel({ api, seal, onRefresh }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Seal image exceeds 4 MB"); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/treasury-onboarding/seal", fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Corporate seal impression saved", {
        description: "It will be painted inside the gold ring on every new Board Resolution.",
      });
      await onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seal upload failed");
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!confirm("Remove the corporate seal impression from all future resolutions?")) return;
    setBusy(true);
    try {
      await api.delete("/admin/treasury-onboarding/seal");
      toast.success("Seal removed");
      await onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };
  const openPreview = async () => {
    try {
      const r = await api.get("/admin/treasury-onboarding/seal/preview",
                              { responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(r.data));
    } catch (e) { toast.error("Could not preview seal"); }
  };
  return (
    <div data-testid="onb-seal-panel"
          className="border border-[#D8B23E]/30 rounded-sm p-5 md:p-6 bg-gradient-to-br from-[#0A0906] via-[#12100A] to-[#0A0906] flex flex-col md:flex-row md:items-center gap-6">
      {/* Glowing gold ring preview */}
      <div className="relative w-32 h-32 rounded-full grid place-items-center shrink-0"
            style={{
              background: "radial-gradient(circle, rgba(216,178,62,0.15) 0%, transparent 70%)",
              boxShadow: "0 0 40px rgba(216,178,62,0.30) inset, 0 0 30px rgba(216,178,62,0.20)",
              border: "1.5px solid #D8B23E",
            }}>
        <div className="absolute inset-2 rounded-full border border-dashed border-[#D8B23E]/60" />
        {seal.present ? (
          <img data-testid="onb-seal-image"
                src={`${api.defaults?.baseURL || ""}/admin/treasury-onboarding/seal/preview?t=${seal.uploaded_at || ""}`}
                alt="Corporate seal"
                onError={openPreview}
                className="w-24 h-24 object-contain" />
        ) : (
          <div className="text-center text-[#D8B23E]">
            <Stamp size={22} className="mx-auto" />
            <div className="font-grotesk text-[8px] mt-1 uppercase tracking-[0.2em]">Empty Ring</div>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#D8B23E]">
          {seal.present ? "Sovereign Seal Loaded" : "No Seal on File"}
        </div>
        <div className="font-serif text-xl text-[#F5F5F0] mt-1">
          {seal.present ? "Every new resolution will bear this seal." : "Upload a scanned PNG of the Vinci physical seal."}
        </div>
        {seal.present && (
          <div className="mt-1 text-[11px] text-[#8A8A82] font-body">
            {seal.filename} · {(seal.size_bytes / 1024).toFixed(0)} KB
            {seal.uploaded_at ? ` · ${new Date(seal.uploaded_at).toLocaleString()}` : ""}
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*"
                 data-testid="onb-seal-input"
                 className="hidden"
                 onChange={(e) => upload(e.target.files?.[0])} />
          <Button
            data-testid="onb-seal-upload"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.24em] text-[10px]">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <><Upload size={12} className="mr-1" /> {seal.present ? "Replace Seal" : "Upload Seal"}</>}
          </Button>
          {seal.present && (
            <>
              <Button
                data-testid="onb-seal-preview"
                onClick={openPreview}
                variant="outline" size="sm"
                className="border-[#D8B23E]/40 text-[#D8B23E] hover:bg-[#D8B23E]/10 font-grotesk uppercase tracking-[0.24em] text-[10px]">
                <Eye size={12} className="mr-1" /> Preview
              </Button>
              <Button
                data-testid="onb-seal-delete"
                onClick={remove}
                variant="outline" size="sm"
                className="border-[#E5484D]/40 text-[#E5484D] hover:bg-[#E5484D]/10 font-grotesk uppercase tracking-[0.24em] text-[10px]">
                <Trash2 size={12} className="mr-1" /> Remove
              </Button>
            </>
          )}
        </div>
      </div>
      {/* Standalone preview dialog */}
      <Dialog open={!!previewUrl} onOpenChange={(v) => {
        if (!v && previewUrl) URL.revokeObjectURL(previewUrl);
        if (!v) setPreviewUrl(null);
      }}>
        <DialogContent className="bg-[#050505] border border-[#D8B23E]/60 text-[#F5F5F0] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl inline-flex items-center gap-2">
              <Stamp size={16} className="text-[#D8B23E]" /> Corporate Seal Preview
            </DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <img src={previewUrl} alt="Corporate seal"
                  className="w-full max-h-[60vh] object-contain bg-black rounded-sm" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DispatchRow({ d }) {
  const ok = d.mail_status === "sent";
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`onb-dispatch-${d.id}`}
      className={`border rounded-sm p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${
        ok ? "border-[#2FB999]/30 bg-[#02150F]/40" : "border-[#E5484D]/30 bg-[#1A0505]/40"
      }`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded-sm text-[9px] font-grotesk uppercase tracking-[0.22em] border inline-flex items-center gap-1 ${
            ok ? "border-[#2FB999]/40 text-[#2FB999] bg-[#2FB999]/10"
                : "border-[#E5484D]/40 text-[#E5484D] bg-[#E5484D]/10"
          }`}>
            {ok ? <><MailCheck size={11} /> Transmitted</> : <><AlertTriangle size={11} /> Failed</>}
          </span>
          <span className="font-serif text-lg text-[#F5F5F0]">{d.bank_name}</span>
        </div>
        <div className="mt-1 text-[12px] font-body text-[#8A8A82]">
          {d.manager_name} · {d.manager_email}
        </div>
        <div className="mt-1 inline-flex items-center gap-2 text-[10px] font-mono text-[#D8B23E]">
          <Sparkles size={10} /> {d.ref}
          <span className="text-[#8A8A82] normal-case font-body italic ml-2">
            · {d.transmitted_at ? new Date(d.transmitted_at).toLocaleString() : ""}
          </span>
        </div>
        {d.mail_error && (
          <div className="mt-2 text-[11px] text-[#E5484D] font-body">
            {d.mail_error}
          </div>
        )}
      </div>
      <div className="text-[11px] font-grotesk uppercase tracking-[0.22em] text-[#8A8A82]">
        {(d.documents || []).length} files · {(d.zip_size_bytes / 1024).toFixed(0)} KB
      </div>
    </motion.div>
  );
}

function PreviewDialog({ preview, onDownload, onClose }) {
  return (
    <Dialog open={!!preview} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="onb-doc-preview-dialog"
        className="bg-[#050505] border border-[#D8B23E]/50 text-[#F5F5F0] max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl inline-flex items-center gap-2">
            <Eye size={16} className="text-[#D8B23E]" />
            {preview?.doc?.filename}
          </DialogTitle>
          <DialogDescription className="text-[11px] font-body text-[#8A8A82]">
            {preview?.doc?.slot_label} · uploaded {preview?.doc?.uploaded_at
              ? new Date(preview.doc.uploaded_at).toLocaleString() : ""}
          </DialogDescription>
        </DialogHeader>
        {preview?.url && (
          preview.doc?.content_type?.startsWith("image/")
            ? <img src={preview.url} alt={preview.doc.filename}
                    className="w-full max-h-[70vh] object-contain rounded-sm border border-white/10 bg-black" />
            : <iframe title={preview.doc.filename} src={preview.url}
                       className="w-full h-[70vh] rounded-sm border border-white/10 bg-black" />
        )}
        <DialogFooter className="gap-2">
          <Button data-testid="onb-doc-preview-download"
                   onClick={() => onDownload?.(preview?.doc)}
                   className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.24em] text-[10px]">
            <Download size={12} className="mr-1" /> Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ═════════════════════════════════════════════════════════════════════
// Digital Notary · Stamp Gallery Panel + Picker Dialog
// ═════════════════════════════════════════════════════════════════════
const STAMP_KINDS = [
  { key: "round",     label: "Round Stamp",      hint: "Circular VINCLING · MUMBAI", aspect: "square"   },
  { key: "chorus",    label: "Chorus Stamp",     hint: "For VINCI INC · Proprietor",  aspect: "landscape"},
  { key: "signature", label: "Pen Signature",    hint: "Common Seal · Authorized Signatory",   aspect: "landscape"},
];

function StampGalleryPanel({ api, stamps, onRefresh }) {
  return (
    <div data-testid="onb-stamp-gallery"
          className="border border-[#D8B23E]/30 rounded-sm p-5 md:p-6 bg-gradient-to-br from-[#0A0906] via-[#12100A] to-[#0A0906] space-y-5">
      <div className="grid md:grid-cols-3 gap-4">
        {STAMP_KINDS.map((s) => (
          <StampTile key={s.key} api={api}
                      meta={s} state={stamps?.[s.key] || { present: false }}
                      onRefresh={onRefresh} />
        ))}
      </div>
      {/* Composite preview — chorus + signature merged */}
      <SignedCompositePreview api={api} stamps={stamps} />
    </div>
  );
}

function StampTile({ api, meta, state, onRefresh }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [orient, setOrient] = useState("auto");
  const [ver, setVer] = useState(0);   // cache-buster for the img preview

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Image exceeds 6 MB"); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", meta.key);
      fd.append("orient", orient);
      fd.append("file", file);
      await api.post("/admin/treasury-onboarding/stamps", fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${meta.label} refined and sealed`, {
        description: "Auto-rotated · alpha-keyed · centred.",
      });
      setVer((v) => v + 1);
      await onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm(`Remove the ${meta.label} from the notary vault?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/treasury-onboarding/stamps/${meta.key}`);
      toast.success(`${meta.label} removed`);
      setVer((v) => v + 1);
      await onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const base = api?.defaults?.baseURL || "";
  const previewUrl = state.present
    ? `${base}/admin/treasury-onboarding/stamps/${meta.key}/preview?v=${state.uploaded_at || ""}${ver}`
    : null;

  return (
    <div data-testid={`stamp-tile-${meta.key}`}
          className={`border rounded-sm p-4 space-y-3 ${
            state.present
              ? "border-[#2FB999]/40 bg-[#02150F]/40"
              : "border-dashed border-[#D8B23E]/30 bg-black/30"
          }`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] flex items-center gap-1.5"
                style={{ color: state.present ? "#2FB999" : "#D8B23E" }}>
            {state.present
              ? <><BadgeCheck size={11} /> Inked · Aligned</>
              : <><Stamp size={11} /> Empty</>}
          </div>
          <div className="font-serif text-base text-[#F5F5F0] mt-0.5">{meta.label}</div>
          <div className="text-[10px] text-[#8A8A82] font-body italic">{meta.hint}</div>
        </div>
      </div>
      <div className={`w-full grid place-items-center bg-black/60 border border-white/5 rounded-sm ${
        meta.aspect === "square" ? "aspect-square" : "aspect-[2/1]"
      }`}>
        {previewUrl ? (
          <img data-testid={`stamp-preview-${meta.key}`}
                src={previewUrl} alt={meta.label}
                className="max-w-[85%] max-h-[85%] object-contain"
                style={{ filter: "drop-shadow(0 0 6px rgba(56,40,132,0.4))" }} />
        ) : (
          <div className="text-center text-[#8A8A82]">
            <Stamp size={18} className="mx-auto opacity-40" />
            <div className="font-grotesk text-[8px] mt-1 uppercase tracking-[0.2em]">no image</div>
          </div>
        )}
      </div>
      {/* Orientation selector — only for stamps (not the signature) */}
      {meta.key !== "signature" && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">
            Rotate
          </span>
          {["auto", "0", "90", "180", "270"].map((o) => (
            <button key={o}
                     data-testid={`stamp-orient-${meta.key}-${o}`}
                     onClick={() => setOrient(o)}
                     className={`px-1.5 py-0.5 rounded-sm text-[9px] font-mono border ${
                       orient === o
                         ? "border-[#D8B23E] text-[#D8B23E] bg-[#D8B23E]/10"
                         : "border-white/10 text-[#8A8A82] hover:border-[#D8B23E]/50 hover:text-[#D8B23E]"
                     }`}>
              {o === "auto" ? "AUTO" : `${o}°`}
            </button>
          ))}
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*"
              data-testid={`stamp-input-${meta.key}`}
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])} />
      <div className="flex items-center gap-2">
        <Button size="sm"
                 data-testid={`stamp-upload-${meta.key}`}
                 onClick={() => fileRef.current?.click()} disabled={busy}
                 className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.22em] text-[9px]">
          {busy
            ? <Loader2 size={11} className="animate-spin" />
            : <><Upload size={11} className="mr-1" /> {state.present ? "Replace" : "Upload"}</>}
        </Button>
        {state.present && (
          <Button size="sm" variant="outline"
                   data-testid={`stamp-delete-${meta.key}`}
                   onClick={remove}
                   className="border-[#E5484D]/40 text-[#E5484D] hover:bg-[#E5484D]/10 font-grotesk uppercase tracking-[0.22em] text-[9px]">
            <Trash2 size={10} />
          </Button>
        )}
      </div>
    </div>
  );
}

function SignedCompositePreview({ api, stamps }) {
  const ready = stamps?.signed?.present;
  const base = api?.defaults?.baseURL || "";
  const url = ready
    ? `${base}/admin/treasury-onboarding/stamps/signed/preview?v=${stamps.signed.uploaded_at || ""}`
    : null;
  return (
    <div data-testid="stamp-composite-preview"
          className="border border-[#D8B23E]/30 rounded-sm p-4 flex items-center gap-4">
      <div className="w-40 h-20 grid place-items-center bg-black border border-white/10 rounded-sm shrink-0">
        {url ? (
          <img src={url} alt="Chorus + signature composite"
                className="max-w-[92%] max-h-[92%] object-contain"
                style={{ filter: "drop-shadow(0 0 6px rgba(56,40,132,0.4))" }} />
        ) : (
          <div className="text-center text-[#8A8A82]">
            <Stamp size={16} className="mx-auto opacity-40" />
            <div className="font-grotesk text-[8px] mt-1 uppercase tracking-[0.2em]">waiting</div>
          </div>
        )}
      </div>
      <div className="min-w-0">
        <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#D8B23E]">
          Chorus + Signature · Auto-Composite
        </div>
        <div className="font-serif text-base text-[#F5F5F0] mt-0.5">
          {ready ? "Composite ready to affix" : "Upload chorus + signature to generate"}
        </div>
        <div className="text-[11px] text-[#8A8A82] font-body italic mt-0.5">
          Signature is centred horizontally and vertically inside the middle band of the chorus stamp so it reads as one ink impression on every senior-management PDF.
        </div>
      </div>
    </div>
  );
}

function StampPickerDialog({ picker, stamps, api }) {
  const [choice, setChoice] = useState("chorus");
  useEffect(() => {
    if (!picker) return;
    // Pick a sensible default the first time the picker opens.
    if (stamps?.signed?.present || stamps?.chorus?.present) setChoice("chorus");
    else if (stamps?.round?.present) setChoice("round");
    else setChoice("none");
  }, [picker, stamps]);

  const options = [
    {
      key: "chorus",
      title: "Chorus + Signature",
      blurb: "The 'For VINCI INC / signature / Proprietor' impression on the signature block.",
      disabled: !(stamps?.signed?.present || stamps?.chorus?.present),
      picked: () => ["signed"],
    },
    {
      key: "round",
      title: "Round Stamp Only",
      blurb: "Circular VINCLING · MUMBAI seal beside the signature.",
      disabled: !stamps?.round?.present,
      picked: () => ["round"],
    },
    {
      key: "both",
      title: "Both · Round + Chorus",
      blurb: "Round seal on the left, chorus over the signature — the full bureaucratic set.",
      disabled: !(stamps?.round?.present
                    && (stamps?.signed?.present || stamps?.chorus?.present)),
      picked: () => ["round", "signed"],
    },
    {
      key: "none",
      title: "Signature Only",
      blurb: "Skip the ink stamps, keep the printed gold signature.",
      disabled: false,
      picked: () => [],
    },
  ];

  const base = api?.defaults?.baseURL || "";
  const activePreview =
    (choice === "chorus" && (stamps?.signed?.present || stamps?.chorus?.present))
      ? `${base}/admin/treasury-onboarding/stamps/${stamps?.signed?.present ? "signed" : "chorus"}/preview`
      : (choice === "round" && stamps?.round?.present)
        ? `${base}/admin/treasury-onboarding/stamps/round/preview`
        : null;

  return (
    <Dialog open={!!picker} onOpenChange={(v) => { if (!v && picker) picker.onCancel(); }}>
      <DialogContent data-testid="stamp-picker-dialog"
                      className="bg-[#050505] border border-[#D8B23E]/60 text-[#F5F5F0] max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl inline-flex items-center gap-2">
            <Stamp size={16} className="text-[#D8B23E]" /> {picker?.title || "Digital Notary"}
          </DialogTitle>
          <DialogDescription className="text-[12px] font-body text-[#C9C9C2]">
            {picker?.blurb || "Choose which impression to affix on this document."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-2">
            {options.map((o) => (
              <button key={o.key}
                       data-testid={`stamp-picker-${o.key}`}
                       disabled={o.disabled}
                       onClick={() => setChoice(o.key)}
                       className={`w-full text-left p-3 rounded-sm border transition-all ${
                         o.disabled
                           ? "border-white/5 bg-black/20 opacity-40 cursor-not-allowed"
                           : choice === o.key
                             ? "border-[#D8B23E] bg-[#D8B23E]/10 shadow-[0_0_24px_-8px_rgba(216,178,62,0.5)]"
                             : "border-white/10 bg-black/40 hover:border-[#D8B23E]/60"
                       }`}>
                <div className="flex items-center gap-2">
                  <span className={`w-3.5 h-3.5 rounded-full border ${
                    choice === o.key
                      ? "border-[#D8B23E] bg-[#D8B23E]"
                      : "border-white/40"
                  }`} />
                  <span className="font-grotesk text-[10px] uppercase tracking-[0.22em]"
                         style={{ color: choice === o.key ? "#D8B23E" : "#F5F5F0" }}>
                    {o.title}
                  </span>
                </div>
                <div className="text-[11px] text-[#8A8A82] font-body italic mt-1 leading-relaxed pl-5">
                  {o.blurb}{o.disabled ? " · asset not uploaded yet" : ""}
                </div>
              </button>
            ))}
          </div>
          <div className="border border-white/10 rounded-sm bg-black grid place-items-center min-h-[180px] p-4">
            {activePreview ? (
              <img data-testid="stamp-picker-active-preview"
                    src={activePreview} alt="Selected impression"
                    className="max-w-full max-h-[220px] object-contain"
                    style={{ filter: "drop-shadow(0 0 8px rgba(56,40,132,0.4))" }} />
            ) : (
              <div className="text-center text-[#8A8A82]">
                <Stamp size={22} className="mx-auto opacity-40" />
                <div className="font-grotesk text-[9px] mt-2 uppercase tracking-[0.22em]">
                  {choice === "both" ? "Both stamps will be affixed side-by-side" : "Signature only · no ink stamp"}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline"
                   data-testid="stamp-picker-cancel"
                   onClick={() => picker?.onCancel()}
                   className="border-white/20 text-[#C9C9C2] font-grotesk uppercase tracking-[0.24em] text-[10px]">
            Cancel
          </Button>
          <Button data-testid="stamp-picker-confirm"
                   onClick={() => {
                     const opt = options.find((o) => o.key === choice);
                     picker?.onPick(opt ? opt.picked() : []);
                   }}
                   className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.24em] text-[10px]">
            <Stamp size={11} className="mr-1" /> Affix & Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
